/* eslint-disable react/require-default-props */
import { TextLeaf } from '@typedream/components';
import { DreamValue } from '@typedream/core';
import {
  createPlugins as createPlatePlugins,
  PlatePlugin,
  PlateProvider,
  PluginOptions,
  RenderLeafFn,
  usePlateActions,
  usePlateSelectors,
  useResetPlateEditor,
} from '@udecode/plate-core';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Canvas } from './components/Canvas';
import { createAccordionPlugin } from './nodes/accordion';
import { createAnimatedTextPlugin } from './nodes/animatedText';
import { createBlockquotePlugin } from './nodes/blockquote';
import { createButtonPlugin } from './nodes/button';
import { createCodeBlockPlugin } from './nodes/codeBlock';
import { createCollectionPlugin } from './nodes/collection';
import { createColumnPlugin } from './nodes/column';
import { createContainerPlugin } from './nodes/container';
import { createDividerPlugin } from './nodes/divider';
import { createHeadingPlugin } from './nodes/heading';
import { createImagePlugin } from './nodes/image';
import { createLinkPlugin } from './nodes/link';
import { createListPlugin } from './nodes/list';
import { createMediaEmbedPlugin } from './nodes/mediaEmbed';
import { createParagraphPlugin } from './nodes/paragraph';
import { createRootPlugin } from './nodes/root';
import { createSpacerPlugin } from './nodes/spacer';
import { createBasicMarksPlugin, createStyledTextPlugin } from './nodes/text';
import { createFormInputPlugin, createFormPlugin } from './nodes/form';
import { OverlayProvider } from './overlay/context';
import { useTPlateSelectors } from './plate';
import { createAutoFormatPlugin } from './plugins/autoFormat';
import { createBadgesPlugin } from './plugins/badges';
import { createCopyPastePlugin } from './plugins/copyPasteDuplicate';
import { createDndPlugin } from './plugins/dnd';
import { createElementSelectionPlugin } from './plugins/elementSelection';
import { createEmitterPlugin } from './plugins/emitter';
import { createExitBreakPlugin } from './plugins/exitBreak';
import { createNodeIdPlugin } from './plugins/nodeId';
import { createShortcutsPlugin } from './plugins/shortcuts';
import { createSingleChildPlugin } from './plugins/singleChild';
import { createSlashCommandPlugin } from './plugins/slashCommand';
import { createSoftBreakPlugin } from './plugins/softBreak';
import { createTextEditorStorePlugin } from './plugins/store';
import { OverlayToolbar, SideToolbar } from './toolbars';
import { ProviderProps } from './types';
import { createNavbarPlugin } from './nodes/navbar/createNavbarPlugin';
import { createInternalLinkPlugin } from './plugins/internalLink/createInternalLinkPlugin';
import { createAiPlugin } from './plugins/ai';
import { createSectionTemplatePlugin } from './plugins/sectionTemplate';
import { createCollectionDataPlugin } from './nodes/collection/createCollectionDataPlugin';
import { ProviderPropsContext } from './context';

const createPlugins = (
  pluginsOverride: ProviderProps['pluginsOverride'] = {}
): PlatePlugin<PluginOptions, DreamValue>[] => {
  const allPlugins = [
    createNodeIdPlugin(),
    createBasicMarksPlugin(),
    createStyledTextPlugin(),
    createRootPlugin(),
    createAiPlugin(),
    createParagraphPlugin(),
    createHeadingPlugin(),
    createContainerPlugin(),
    createColumnPlugin(),
    createSpacerPlugin(),
    createDividerPlugin(),
    createBlockquotePlugin(),
    createFormPlugin(),
    createFormInputPlugin(),
    createButtonPlugin(),
    createImagePlugin(),
    createLinkPlugin(),
    createListPlugin(),
    createMediaEmbedPlugin(),
    createCodeBlockPlugin(),
    createAccordionPlugin(),
    createCollectionPlugin<PluginOptions>(),
    createCollectionDataPlugin<PluginOptions>(),
    createAnimatedTextPlugin(),
    createSoftBreakPlugin() as unknown as PlatePlugin<PluginOptions, DreamValue>,
    createExitBreakPlugin() as unknown as PlatePlugin<PluginOptions, DreamValue>,
    createAutoFormatPlugin() as unknown as PlatePlugin<PluginOptions, DreamValue>,
    createTextEditorStorePlugin<PluginOptions>(),
    createEmitterPlugin(),
    createElementSelectionPlugin(),
    createDndPlugin(),
    createSlashCommandPlugin(),
    createBadgesPlugin(),
    createCopyPastePlugin(),
    createShortcutsPlugin(),
    createSingleChildPlugin(),
    createNavbarPlugin(),
    createInternalLinkPlugin(),
    createSectionTemplatePlugin(),
  ];

  const filterPluginsRecursively = (plugins: typeof allPlugins, filteredOutKeys: string[]) =>
    plugins.reduce((acc, cur) => {
      if (!filteredOutKeys.includes(cur.key)) {
        const plugin = { ...cur };
        if (plugin.plugins) {
          plugin.plugins = filterPluginsRecursively(plugin.plugins, filteredOutKeys);
        }
        acc.push(plugin);
      }

      return acc;
    }, [] as typeof allPlugins);

  const usedPlugins = filterPluginsRecursively(
    allPlugins,
    Object.entries(pluginsOverride)
      .filter(([, v]) => v === false)
      .map(([k]) => k)
  );

  return createPlatePlugins(usedPlugins, {
    overrideByKey: Object.fromEntries(
      Object.entries(pluginsOverride).filter(([k, v]) => !!k && v !== false) as any
    ),
  });
};

const defaultInitialContent = [
  {
    type: 'root',
    vertical_align: 'align_start',
    children: [
      {
        type: 'spacer',
        height: 70,
        children: [{ text: '' }],
      },
      {
        type: 'p',
        children: [{ text: '' }],
      },
    ],
  },
] as unknown as DreamValue;

// a simple wrapper component that we will pass a key to force remount
// when editor instance is changed
function DreamEditorContent({ children }: { children: ReactNode }) {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
}

function DreamEditorContainer(props: { children: ReactNode }) {
  const { children } = props;

  const { fileManager, id, onSave } = useContext(ProviderPropsContext);

  const editor = useTPlateSelectors(id).editor();

  useEffect(() => {
    // We assign `fileEditor` property to editor,
    // so it can be used outside of react component
    // like in a regular function as long we have reference to editor
    editor.fileManager = fileManager;
    editor.onSave =
      onSave ||
      (() => {
        /* default: do nothing */
      });
  });

  return <DreamEditorContent key={editor.key}>{children}</DreamEditorContent>;
}

function OnChangeHandler({ id, onChange }: Pick<ProviderProps, 'id' | 'onChange'>) {
  const val = usePlateSelectors<DreamValue>(id).value();
  useEffect(() => {
    if (onChange) onChange(val);
  }, [val]);
  return null;
}

const renderLeaf: RenderLeafFn<DreamValue> = (textProps) => <TextLeaf {...textProps} />;

function Provider(props: ProviderProps) {
  const { children, id, initialContent, onChange, pluginsOverride } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return (
    // We temporarily add context={window} props here
    // to fix the issue of duplicate DndProvider in the app level
    <DndProvider context={window} backend={HTML5Backend}>
      <PlateProvider
        id={id}
        plugins={createPlugins(pluginsOverride)}
        normalizeInitialValue
        initialValue={initialContent || defaultInitialContent}
        renderLeaf={renderLeaf}
      >
        <OnChangeHandler id={id} onChange={onChange} />
        <ProviderPropsContext.Provider value={props}>
          <OverlayProvider>
            {/* <DreamEditorSelectionDebugger id={id} /> */}
            <DreamEditorContainer>{children}</DreamEditorContainer>
          </OverlayProvider>
        </ProviderPropsContext.Provider>
      </PlateProvider>
    </DndProvider>
  );
}

export const DreamEditor = {
  Canvas,
  OverlayToolbar,
  Provider,
  SideToolbar,
};

export const useSetDreamEditorContent = () => {
  const { id, initialContent } = useContext(ProviderPropsContext);
  const setPlateEditorValue = usePlateActions(id).value();
  const resetPlateEditor = useResetPlateEditor(id);

  // if passed undefined or null then the initial content will be restored
  const setContent = (value?: DreamValue | null) => {
    setPlateEditorValue(value || initialContent || defaultInitialContent);
    // All plugins will be returned to it's initial value.
    // You'll lose history and all other custom values stored by plugins
    resetPlateEditor();
  };

  return setContent;
};
